import { useState, useCallback, useMemo } from 'react';
import { useWindowSize } from 'app/spacing';
import { editableDashlet } from 'pages/Dashboard/util';
import { REPORT_TYPES } from 'components/Wizards/Dashlet/types';
import { normalizeDateFilter } from 'components/DashboardGrid/utils';
import { deferExecution } from 'utility/defer';

const dateFilterEqual = (a, b) => {
  return (
    a.start === b.start &&
    a.end === b.end &&
    a.selectedIndex === b.selectedIndex
  );
};

export const useDateRangePickerShortcut = ({
  ref,
  currentDashlet,
  editDashlet,
  onRefresh,
  showDateShortcut,
  ranges,
  t,
}) => {
  const { width } = useWindowSize();
  const [datePickerDirection, setDatePickerDirection] = useState('left');

  const containerRef = (elem) => {
    ref.current = elem;
    const box = elem?.getBoundingClientRect();
    const right = box?.right ?? 0;
    const buffer = width - 650;

    if (right > buffer) {
      return setDatePickerDirection('left');
    }
    return setDatePickerDirection('right');
  };

  const dateFilterValue = useMemo(() => {
    if (!showDateShortcut) {
      return {};
    }

    const filter = normalizeDateFilter(
      currentDashlet?.config?.feExtraInfo?.dateFilter,
      ranges
    );

    return {
      range: filter && [filter.start, filter.end],
      selectedIndex: filter?.selectedIndex,
    };
  }, [showDateShortcut, currentDashlet, ranges]);

  const updateDashletDateFilter = useCallback(
    async (range, selectedIndex) => {
      const newDateFilter = {
        start: range[0],
        end: range[1],
        selectedIndex,
      };

      if (
        dateFilterEqual(
          newDateFilter,
          currentDashlet.config.feExtraInfo?.dateFilter ?? {}
        )
      ) {
        return;
      }

      const newDashlet = {
        ...editableDashlet(currentDashlet, t),
        dateFilter: newDateFilter,
      };

      // email interaction stats has an instance of useMangedQuery that we pass the date filter state directly to
      const optimisticUpdate =
        currentDashlet?.config?.reportType === REPORT_TYPES.INTERACTION_STATS;

      await editDashlet(newDashlet, null, optimisticUpdate);
      if (onRefresh) {
        deferExecution(() => onRefresh());
      }
    },
    [editDashlet, onRefresh, currentDashlet, t]
  );

  const unitsWidth = currentDashlet?.layout?.w;

  return {
    datePickerDirection,
    containerRef,
    updateDashletDateFilter,
    dateFilterValue,
    unitsWidth,
  };
};
